import React from 'react';
import AuthedImage from './AuthedImage';
import { useAuth } from '../../context/AuthContext';

const SIZES = { xs: 22, sm: 28, md: 36, lg: 48, xl: 88 };

/**
 * A person, as a circle.
 *
 * Falls back to initials on a colour derived from the person rather than to a
 * grey silhouette: most people will never upload a picture, so the fallback is
 * the normal state and it should still tell one person from another.
 *
 * A picture lives behind `/api/users/:id/avatar`, which sits behind the bearer
 * token like every other media route here — hence AuthedImage rather than a
 * bare <img src>, which would 401 and render broken.
 */

/** Stable hue per person, kept off the brand teal so nobody reads as chrome. */
function hueFor(seed) {
  const s = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h > 145 && h < 205 ? (h + 120) % 360 : h;
}

function initialsOf(user) {
  const first = user?.firstName ?? user?.first_name ?? '';
  const last = user?.lastName ?? user?.last_name ?? '';
  const both = `${first?.[0] || ''}${last?.[0] || ''}`.trim();
  if (both) return both.toUpperCase();
  const name = user?.name || user?.stage_name || '';
  return name.trim().charAt(0).toUpperCase() || '?';
}

function Avatar({ user, size = 'md', className = '', title }) {
  const { token } = useAuth();
  const px = typeof size === 'number' ? size : (SIZES[size] || SIZES.md);
  const initials = initialsOf(user);
  const hue = hueFor(user?.id ?? user?.user_id ?? initials);

  const style = {
    width: px,
    height: px,
    borderRadius: '50%',
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: Math.max(9, Math.round(px * 0.38)),
    letterSpacing: '0.01em',
    color: `hsl(${hue} 70% 74%)`,
    background: `hsl(${hue} 42% 18%)`,
    boxShadow: `inset 0 0 0 1px hsl(${hue} 45% 30%)`,
    overflow: 'hidden',
    objectFit: 'cover',
  };

  const fallback = (
    <div className={['user-avatar', className].filter(Boolean).join(' ')} style={style} title={title} aria-hidden={!title}>
      {initials}
    </div>
  );

  // `avatarUrl` is what the API returns; `avatarUrl`-less users are the norm.
  const url = user?.avatarUrl || user?.avatar_url;
  if (!url || !token) return fallback;

  return (
    <AuthedImage
      src={url}
      token={token}
      alt={title || ''}
      title={title}
      className={className}
      style={{ ...style, boxShadow: 'inset 0 0 0 1px var(--border)' }}
      fallback={fallback}
    />
  );
}

export default Avatar;
